import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";

import ActionButton from "../../formInputs/buttons/ActionButton";
import DiffRatingGrid from "./DiffRatingGrid";
import LoadingSvg2 from "../../loadingSvg/LoadingSvg2";
import useAutoSave from "../../../hooks/useAutoSave";
import useDiffRatingSave from "../../../hooks/ratings/diff/useDiffRatingSave";
import useDiffRatingState from "../../../hooks/ratings/diff/useDiffRatingState";
import useFlushOnLeave from "../../../hooks/ratings/useFlushOnLeave";
import useRosterDiffRatings from "../../../hooks/ratings/diff/useRosterDiffRatings";
import { isDiffStudentEditable } from "../../../hooks/ratings/diff/isDiffStudentEditable";
import { buildDiffRosterKey, cellKey, gradeTabLabel } from "../../../hooks/ratings/diff/indexRosterDiffRatings";
import isGradingProfileWindowOpen from "../../../utils/ActionControls/isGradingProfileWindowOpen";

/**
 * Differentiated bulk ratings: grade tabs + sparse grid. Own load/save tree.
 */
const DifferentiatedRatings = ({
    actionWindowsDto,
    basePath,
    existingRatingsData,
    groupData,
    mode,
    onSaveSubjectComments,
    onSubjectCommentChange,
    selectedEpr,
    selectedSchoolLocationKey,
    selectedTemplate,
    subjectComments,
    token,
    userDetails
}) => {
    const navigate = useNavigate();
    const {
        grades,
        itemsByGrade,
        studentsByGrade,
        ratings: serverRatings,
        comments: serverComments,
        snapshots,
        students,
        loading,
        reload,
        commitSavedStudents
    } = useRosterDiffRatings({
        mode,
        existingRatingsData,
        groupData,
        selectedEpr,
        selectedTemplate,
        token
    });

    const { ratings, comments, hasChanges, setHasChanges, setRating, setBulkRating, setComment } = useDiffRatingState(
        serverRatings,
        serverComments
    );
    const { saveRatings } = useDiffRatingSave({
        snapshots,
        ratings,
        comments,
        students,
        token,
        userDetails,
        setHasChanges,
        reload,
        commitSavedStudents
    });
    useAutoSave(saveRatings, hasChanges);
    const rosterKey = useMemo(
        () => buildDiffRosterKey({ mode, groupData, existingRatingsData }),
        [mode, groupData, existingRatingsData]
    );
    const leaveKey = `${selectedTemplate?.templateId ?? ""}|${selectedEpr?.key ?? ""}|${rosterKey}`;
    useFlushOnLeave(saveRatings, hasChanges, leaveKey);

    const [activeGrade, setActiveGrade] = useState(null);
    const effectiveGrade = grades.includes(activeGrade) ? activeGrade : grades[0] || null;

    const items = (effectiveGrade && itemsByGrade[effectiveGrade]) || [];
    const studentsAtGrade = useMemo(() => {
        const keys = (effectiveGrade && studentsByGrade[effectiveGrade]) || [];
        return students.filter((s) => keys.includes(s.studentKey));
    }, [students, studentsByGrade, effectiveGrade]);

    const handleBulkRating = (grade, contentId, rating) => {
        const timePeriod = mode === "GROUPS" ? groupData?.[0]?.timePeriod : selectedEpr?.timePeriod;
        const isActiveWindow = isGradingProfileWindowOpen(actionWindowsDto, timePeriod);
        const editableKeys = studentsAtGrade
            .filter((s) => isDiffStudentEditable({ student: s, mode, isActiveWindow, selectedEpr, userDetails }))
            .map((s) => s.studentKey);
        setBulkRating(grade, contentId, rating, editableKeys);
    };

    const handleSaveComment = (studentKey, grade, contentId, comment) => {
        const nextComments = {
            ...comments,
            [cellKey(studentKey, grade, contentId)]: comment ?? ""
        };
        setComment(studentKey, grade, contentId, comment);
        saveRatings({ comments: nextComments });
    };

    if (loading) {
        return <LoadingSvg2 />;
    }

    if (!grades.length) {
        return (
            <div className="diff-empty-setup">
                <span className="diff-empty-setup__text">
                    No differentiation set for any student for the selected subject. This may be intended, but please go
                    to Manage Differentiation as needed to exclude/add content items for any student(s) who receive
                    differentiated instruction.
                </span>
                <div className="diff-empty-setup__btn">
                    <ActionButton
                        label="Manage Differentiation"
                        onClick={() => navigate(`${basePath || "/home"}/summary/differentiation`)}
                    />
                </div>
            </div>
        );
    }

    return (
        <div className="differentiated-ratings">
            <h5 className="diff-grade-panel__label" id="diff-grade-tabs-label">
                Select Grade:
            </h5>
            <div className="diff-grade-panel">
                <div className="diff-grade-panel__header">
                    <div className="diff-grade-tabs__list" role="tablist" aria-labelledby="diff-grade-tabs-label">
                        {grades.map((grade) => {
                            return (
                                <button
                                    key={grade}
                                    type="button"
                                    role="tab"
                                    aria-selected={grade === effectiveGrade}
                                    className={
                                        grade === effectiveGrade
                                            ? "diff-grade-tabs__btn is-active"
                                            : "diff-grade-tabs__btn"
                                    }
                                    onClick={() => setActiveGrade(grade)}
                                >
                                    {gradeTabLabel(grade)}
                                </button>
                            );
                        })}
                    </div>
                </div>
                <div className="diff-grade-panel__body">
                    {hasChanges && <p className="diff-unsaved-hint">Unsaved differentiated ratings</p>}
                    <DiffRatingGrid
                        actionWindowsDto={actionWindowsDto}
                        basePath={basePath}
                        comments={comments}
                        groupData={groupData}
                        hasChanges={hasChanges}
                        items={items}
                        mode={mode}
                        onBulkRating={handleBulkRating}
                        onRatingChange={setRating}
                        onSave={saveRatings}
                        onSaveComment={handleSaveComment}
                        onSaveSubjectComments={onSaveSubjectComments}
                        onSubjectCommentChange={onSubjectCommentChange}
                        ratings={ratings}
                        ratingsLoaded={!loading}
                        selectedEpr={selectedEpr}
                        selectedSchoolLocationKey={selectedSchoolLocationKey}
                        selectedTemplate={selectedTemplate}
                        snapshots={snapshots}
                        students={studentsAtGrade}
                        subjectComments={subjectComments}
                        token={token}
                        userDetails={userDetails}
                    />
                </div>
            </div>
        </div>
    );
};

DifferentiatedRatings.propTypes = {
    actionWindowsDto: PropTypes.array,
    basePath: PropTypes.string,
    existingRatingsData: PropTypes.array,
    groupData: PropTypes.array,
    mode: PropTypes.oneOf(["HOMEROOM", "GROUPS"]).isRequired,
    onSaveSubjectComments: PropTypes.func,
    onSubjectCommentChange: PropTypes.func,
    selectedEpr: PropTypes.object,
    selectedSchoolLocationKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    selectedTemplate: PropTypes.object,
    subjectComments: PropTypes.object,
    token: PropTypes.string,
    userDetails: PropTypes.object
};

export default DifferentiatedRatings;
